import { log } from '../app/logger.js';

/**
 * Manages location visibilty.
 */
class LocationFilter {
  static DayTrips = 'day-trips';
  static Backcountry = 'backcountry';
  static Frontcountry = 'frontcountry';

  // eslint-disable-next-line complexity
  static filter({
    locs,
    filter,
    south,
    north,
    west,
    east,
    gap_lat,
    gap_lng,
    mapZoom,
    testPath,
  }) {
    function traceit(milestone, ...args) {
      if (testPath) {
        testPath.push({
          milestone,
          locs: [...args].map(l =>
            Object.assign({}, l[0], { lat_idx: l[1], lng_idx: l[2] })
          ),
        });
      }
    }

    function dist(l1, l2) {
      return Math.sqrt(
        Math.pow(l1.lat - l2.lat, 2) + Math.pow(l1.lng - l2.lng, 2)
      );
    }
    function cond(l) {
      return l.zoom > mapZoom;
    }
    function greaterThan(l1, l2) {
      return (
        l1.zoom < l2.zoom ||
        (l1.zoom == l2.zoom && l1.wasVisible > l2.wasVisible)
      );
    }

    const maxd = Math.min(gap_lat, gap_lng) / 2;

    const lat_cnt = Math.ceil((north - south) / gap_lat);
    const lng_cnt = Math.abs(Math.ceil((east - west) / gap_lng));

    log(`[map:locfilter-on-map] grid size (${lat_cnt}, ${lng_cnt})`);

    let areas = new Array(lat_cnt);
    for (let i = 0; i < lat_cnt; i++) {
      areas[i] = new Array(lng_cnt);
      for (let j = 0; j < lng_cnt; j++) {
        areas[i][j] = [];
      }
    }

    for (let loc of locs) {
      loc.wasVisible = loc.visible;

      let keywords = this.ActivityGroups.get(filter);
      let is_matched =
        loc.type != 'toponym' ||
        !keywords ||
        keywords.some(k => {
          let activities = loc.attributes && loc.attributes.activities;
          return activities && activities.includes(k);
        });

      // Toss out location if doesn't match the active filter.
      if (!is_matched) {
        traceit(`[hide] loc doesn't matches a filter`, [loc, NaN, NaN]);
        loc.visible = false;
        continue;
      }

      let lat_idx = Math.floor((loc.lat - south) / gap_lat);
      let lng_idx = Math.floor((loc.lng - west) / gap_lng);
      if (
        lat_idx < 0 ||
        lat_idx >= lat_cnt ||
        lng_idx < 0 ||
        lng_idx >= lng_cnt
      ) {
        traceit(`[hide] loc is out of map`, [loc, lat_idx, lng_idx]);
        continue;
      }

      // Hide all conditional locations of lesser weight in nearby area or hide
      // current location if no free spot for it. Skip all unconditional locs.
      let obscured = false;
      for (
        let lat_i = Math.max(lat_idx - 1, 0);
        lat_i <= Math.min(lat_cnt - 1, lat_idx + 1);
        lat_i++
      ) {
        for (
          let lng_i = Math.max(lng_idx - 1, 0);
          lng_i <= Math.min(lng_cnt - 1, lng_idx + 1);
          lng_i++
        ) {
          let area_locs = areas[lat_i][lng_i];
          for (let area_loc of area_locs) {
            if (dist(area_loc, loc) <= maxd) {
              if (greaterThan(loc, area_loc)) {
                if (cond(area_loc)) {
                  // Note, we do not remove hidden locations from the list, and
                  // thus they can affect on other locations visibility, which
                  // could be otherwise visible.
                  traceit(
                    `[hide] loc is obscured by a loc`,
                    [area_loc, lat_i, lng_i],
                    [loc, lat_idx, lng_idx]
                  );
                  area_loc.visible = false;
                }
              } else if (cond(loc)) {
                traceit(
                  `[hide] loc is obscured by a loc`,
                  [loc, lat_idx, lng_idx],
                  [area_loc, lat_i, lng_i]
                );
                loc.visible = false;
                obscured = true;
                break;
              }
            }
          }
          if (obscured) {
            break;
          }
        }
        if (obscured) {
          break;
        }
      }
      if (!obscured) {
        traceit(`[show] loc fits into area`, [loc, lat_idx, lng_idx]);
        areas[lat_idx][lng_idx].push(loc);
        loc.visible = true;
      }
    }
  }

  // Activity type groups by activities.
  static ActivityGroups = new Map([
    [this.DayTrips, ['day use']],
    [this.Backcountry, ['backcountry camping']],
    [this.Frontcountry, ['car camping', 'RVing', 'front country camping']],
  ]);

  static logPath(testPath) {
    let strs = [];
    for (let s of testPath) {
      let i = 0;
      let milestone = s.milestone.replace(
        /loc/g,
        () =>
          `'${s.locs[i].name}' loc at (${s.locs[i].lat_idx}, ${
            s.locs[i++].lng_idx
          })`
      );
      strs.push(`${milestone}`);
    }
    return strs;
  }
}

export { LocationFilter };
